import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { companyApi } from '../api/company.api';
import { Company, CompanyStats } from '../types/user';
import { toast } from '../utils/toast';

export const CompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'deleted'>(
    'all'
  );
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  if (!user?.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await companyApi.listCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = async (companyId: string) => {
    try {
      const response = await companyApi.getCompanyDetails(companyId);
      setCompanyStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load company details:', error);
    }
  };

  const handleViewDetails = async (company: Company) => {
    setSelectedCompany(company);
    await loadCompanyDetails(company._id);
  };

  const handleStatusChange = async (
    companyId: string,
    status: 'active' | 'suspended' | 'deleted'
  ) => {
    try {
      await companyApi.updateCompanyStatus(companyId, status);
      await loadCompanies();
      if (selectedCompany?._id === companyId) {
        setSelectedCompany(null);
        setCompanyStats(null);
      }
    } catch (error) {
      console.error('Failed to update company status:', error);
      alert(t('companies.statusChange.failed'));
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete || deleteConfirmName !== companyToDelete.name) return;
    try {
      setDeleteLoading(true);
      const result = await companyApi.deleteCompany(companyToDelete._id);
      const { deleted_vehicles, deleted_users, deleted_trips } = result.data;
      toast.success(
        t('companies.delete.successSummary', {
          vehicles: deleted_vehicles,
          users: deleted_users,
          trips: deleted_trips,
        })
      );
      setCompanyToDelete(null);
      setDeleteConfirmName('');
      await loadCompanies();
    } catch (error: any) {
      console.error('Failed to delete company:', error);
      toast.error(error.response?.data?.message || t('companies.delete.failed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGpsVisibilityChange = async (companyId: string, enabled: boolean) => {
    await companyApi.updateGpsVisibility(companyId, enabled);
    // Refresh the companies list so the updated flag is reflected
    await loadCompanies();
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      deleted: 'bg-red-100 text-red-800',
    };
    const statusLabels: Record<string, string> = {
      active: t('companies.statusActive'),
      suspended: t('companies.statusSuspended'),
      deleted: t('companies.statusDeleted'),
    };
    return (
      <span
        className={`${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'} px-2 py-1 rounded-full text-xs font-medium`}
      >
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPlanBadge = (plan: string) => {
    const styles = {
      trial: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-indigo-100 text-indigo-800',
    };
    return (
      <span
        className={`${styles[plan as keyof typeof styles] || 'bg-gray-100 text-gray-800'} px-2 py-1 rounded-full text-xs font-medium`}
      >
        {t(`companies.plans.${plan}`) || plan}
      </span>
    );
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch =
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('companies.title')}</h1>
          <p className="text-gray-600 mt-1">{t('companies.subtitle')}</p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex gap-4">
              <input
                type="text"
                placeholder={t('companies.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | 'active' | 'suspended' | 'deleted')
                }
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="all">{t('companies.allStatus')}</option>
                <option value="active">{t('companies.statusActive')}</option>
                <option value="suspended">{t('companies.statusSuspended')}</option>
                <option value="deleted">{t('companies.statusDeleted')}</option>
              </select>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
            >
              {t('companies.createCompany')}
            </button>
          </div>
        </div>

        {/* Companies Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.company')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.plan')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.vehicleLimit')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.created')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companies.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      {t('companies.noCompanies')}
                    </td>
                  </tr>
                ) : (
                  filteredCompanies.map((company) => (
                    <tr key={company._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{company.name}</div>
                          <div className="text-sm text-gray-500">{company.slug}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getPlanBadge(company.subscription.plan)}</td>
                      <td className="px-6 py-4">{getStatusBadge(company.status)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {company.subscription.vehicleLimit}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(company.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(company)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          {t('companies.table.viewDetails')}
                        </button>
                        {company.status === 'active' && (
                          <button
                            onClick={() => handleStatusChange(company._id, 'suspended')}
                            className="text-yellow-600 hover:text-yellow-900 mr-4"
                          >
                            {t('companies.table.suspend')}
                          </button>
                        )}
                        {company.status === 'suspended' && (
                          <button
                            onClick={() => handleStatusChange(company._id, 'active')}
                            className="text-green-600 hover:text-green-900 mr-4"
                          >
                            {t('companies.table.activate')}
                          </button>
                        )}
                        <button
                          onClick={() => { setCompanyToDelete(company); setDeleteConfirmName(''); }}
                          className="text-red-600 hover:text-red-900"
                          title={t('companies.delete.button')}
                        >
                          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Company Details Modal */}
        {selectedCompany && (
          <CompanyDetailsModal
            company={selectedCompany}
            stats={companyStats}
            onClose={() => {
              setSelectedCompany(null);
              setCompanyStats(null);
            }}
            onStatusChange={handleStatusChange}
            onGpsVisibilityChange={handleGpsVisibilityChange}
          />
        )}

        {/* Create Company Modal */}
        {showCreateModal && (
          <CreateCompanyModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadCompanies();
            }}
          />
        )}

        {/* Delete Company Confirmation Modal */}
        {companyToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
              <h2 className="text-lg font-bold text-gray-900">{t('companies.delete.modalTitle')}</h2>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700 font-medium">{t('companies.delete.warning')}</p>
              </div>
              <p className="text-sm text-gray-600">
                {t('companies.delete.confirmLabel')} :{' '}
                <span className="font-semibold text-gray-900">{companyToDelete.name}</span>
              </p>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={t('companies.delete.confirmPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
              />
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => { setCompanyToDelete(null); setDeleteConfirmName(''); }}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('companies.delete.cancelButton')}
                </button>
                <button
                  onClick={handleDeleteCompany}
                  disabled={deleteConfirmName !== companyToDelete.name || deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? t('companies.delete.deleting') : t('companies.delete.confirmButton')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Company Details Modal Component
const CompanyDetailsModal: React.FC<{
  company: Company;
  stats: CompanyStats | null;
  onClose: () => void;
  onStatusChange: (companyId: string, status: 'active' | 'suspended' | 'deleted') => void;
  onGpsVisibilityChange: (companyId: string, enabled: boolean) => Promise<void>;
}> = ({ company, stats, onClose, onStatusChange, onGpsVisibilityChange }) => {
  const { t } = useTranslation();
  const [gpsToggleLoading, setGpsToggleLoading] = React.useState(false);
  const [gpsVisible, setGpsVisible] = React.useState(
    company.gps_data_visible_to_company_users ?? false
  );

  const handleGpsToggle = async () => {
    const newValue = !gpsVisible;
    setGpsToggleLoading(true);
    try {
      await onGpsVisibilityChange(company._id, newValue);
      setGpsVisible(newValue);
      toast.success(
        newValue
          ? t('companies.detailsModal.gpsEnabledSuccess')
          : t('companies.detailsModal.gpsDisabledSuccess')
      );
    } catch {
      toast.error(t('companies.detailsModal.gpsToggleFailed'));
    } finally {
      setGpsToggleLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{company.name}</h2>
              <p className="text-gray-600">{company.slug}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Subscription Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('companies.detailsModal.subscription')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{t('companies.detailsModal.plan')}</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {t(`companies.plans.${company.subscription.plan}`)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{t('companies.detailsModal.vehicleLimit')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {company.subscription.vehicleLimit}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{t('companies.detailsModal.status')}</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">{company.status}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{t('companies.detailsModal.startDate')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(company.subscription.startDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('companies.detailsModal.statistics')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">
                    {t('companies.detailsModal.users')}
                  </p>
                  <p className="text-2xl font-bold text-blue-900">{stats.users.total}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {stats.users.active} {t('companies.detailsModal.active')}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">
                    {t('companies.detailsModal.vehicles')}
                  </p>
                  <p className="text-2xl font-bold text-green-900">{stats.vehicles.total}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {stats.vehicles.active} {t('companies.detailsModal.active')}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">
                    {t('companies.detailsModal.trips')}
                  </p>
                  <p className="text-2xl font-bold text-purple-900">{stats.trips.total}</p>
                  <p className="text-xs text-purple-600 mt-1">
                    {stats.trips.qualified} {t('companies.detailsModal.qualified')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* GPS Visibility Toggle */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('companies.detailsModal.dataSettings')}
            </h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex-1 mr-4">
                <p className="text-sm font-medium text-gray-900">
                  {t('companies.detailsModal.gpsVisibilityLabel')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('companies.detailsModal.gpsVisibilityDescription')}
                </p>
              </div>
              <button
                onClick={handleGpsToggle}
                disabled={gpsToggleLoading}
                aria-label={t('companies.detailsModal.gpsVisibilityLabel')}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  gpsVisible ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    gpsVisible ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {company.status === 'active' && (
              <button
                onClick={() => onStatusChange(company._id, 'suspended')}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                {t('companies.detailsModal.suspendCompany')}
              </button>
            )}
            {company.status === 'suspended' && (
              <button
                onClick={() => onStatusChange(company._id, 'active')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {t('companies.detailsModal.activateCompany')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('companies.detailsModal.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Company Modal Component
const CreateCompanyModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'trial' as 'trial' | 'basic' | 'premium' | 'enterprise',
    vehicleLimit: 5,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await companyApi.createCompany({
        name: formData.name,
        slug: formData.slug,
        subscription: {
          plan: formData.plan,
          vehicleLimit: formData.vehicleLimit,
        },
      });
      onSuccess();
    } catch (error: any) {
      setError(error.response?.data?.message || t('companies.createModal.failedCreate'));
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('companies.createModal.title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.createModal.companyName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder={t('companies.createModal.companyNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.createModal.slug')}
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder={t('companies.createModal.slugPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('companies.createModal.slugHelper')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.createModal.plan')}
                </label>
                <select
                  value={formData.plan}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      plan: e.target.value as 'trial' | 'basic' | 'premium' | 'enterprise',
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="trial">{t('companies.plans.trial')}</option>
                  <option value="basic">{t('companies.plans.basic')}</option>
                  <option value="premium">{t('companies.plans.premium')}</option>
                  <option value="enterprise">{t('companies.plans.enterprise')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.createModal.vehicleLimit')}
                </label>
                <input
                  type="number"
                  value={formData.vehicleLimit}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicleLimit: parseInt(e.target.value) })
                  }
                  min="1"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('companies.createModal.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('companies.createModal.creating') : t('companies.createModal.createCompany')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
